/**
 * Phase 4 base FE — Compliance → Queries tab.
 *
 * Replaces the "coming soon" stub with a real list surface that reads
 * from `/compliance/queries` and applies Jawahar's Query Tracking
 * filters (status + category + assignee) + AC 4.3 closed-terminal
 * behaviour (lock icon; Answer/Close hidden on closed rows; comments
 * still allowed; admin-only Reopen with a recorded reason).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ClipboardList,
  Clock,
  ExternalLink,
  Loader2,
  Lock,
  MessageSquare,
  MessageSquarePlus,
  RotateCcw,
  ShieldCheck,
  X,
} from 'lucide-react';
import {
  compliance,
  siteUsers,
  studies,
  subjects,
  queryTypeFromOrigin,
  type ComplianceQueryRow,
  type DelegatedStudyUser,
  type QueryCategory,
  type StudyRow,
  type SubjectRow,
  type QueryVisibilityType,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { fmtDateTime } from '@/lib/dates';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import type { UserRole } from '@/contexts/AuthContext';
import { DocumentViewerModal } from '@/components/binder/DocumentViewerModal';
import { SingleSelectSearch, type SingleSelectOption } from '@/components/ui/SingleSelectSearch';
import { errorText } from '@/lib/permissionError';
import { studyLabel, studyName } from '@/lib/studyLabel';
import { subjectDisplayId } from '@/lib/subjectLabel';

const CATEGORY_LABELS: Record<QueryCategory, string> = {
  missing_document: 'Missing document',
  incorrect_metadata: 'Incorrect metadata',
  expired_document: 'Expired document',
  compliance_flag: 'Compliance flag',
  other: 'Other',
};

const STATUS_LABELS: Record<ComplianceQueryRow['status'], string> = {
  open: 'Open',
  answered: 'Answered',
  closed: 'Closed',
};

const STATUS_BADGE: Record<ComplianceQueryRow['status'], string> = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  answered: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const CATEGORY_BADGE: Record<QueryCategory, string> = {
  missing_document: 'bg-slate-100 text-slate-700',
  incorrect_metadata: 'bg-purple-100 text-purple-700',
  expired_document: 'bg-danger-100 text-danger-700',
  compliance_flag: 'bg-amber-100 text-amber-800',
  other: 'bg-slate-100 text-slate-600',
};

/**
 * Where a query was raised, as plain text (client, 23 Oct 2026: the PI and
 * team could not tell). Covers every raise point, internal or external:
 * a form field, a whole visit, an eISF folder/document, or the
 * Queries page itself ("General").
 */
function raisedOn(row: ComplianceQueryRow): { kind: string; where: string; context: string } {
  const study = row.study_title || row.study_protocol_number
    ? studyName({ study_id: row.study_id, title: row.study_title, protocol_number: row.study_protocol_number })
    : row.study_reference_number ?? '';
  const subject = row.subject_external_ref
    ? subjectDisplayId({ external_ref: row.subject_external_ref, ivrf_number: row.subject_ivrf_number })
    : '';
  const context = [study, subject].filter(Boolean).join(' · ');
  const join = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' · ');

  if (row.field_id || row.field_ref) {
    return {
      kind: 'Form field',
      where: join(row.visit_name, row.section_ref && row.field_ref ? `${row.section_ref} → ${row.field_ref}` : row.section_ref || row.field_ref),
      context,
    };
  }
  if (row.visit_id) {
    return { kind: 'Visit', where: join(row.visit_name || row.section_ref, 'whole visit'), context };
  }
  if (row.folder_id || row.document_id) {
    return {
      kind: 'eISF binder',
      where: join(row.binder_reference_number, row.section_ref, row.document_title),
      context: study,
    };
  }
  return { kind: 'General', where: row.section_ref && row.section_ref !== 'General' ? row.section_ref : 'Not tied to a form or document', context };
}

/**
 * Who may raise / answer / comment / close / reopen comes from the RBAC grid
 * (Harish, 23 Oct 2026). The role lists below only cover the moment before
 * /me/permissions answers. Close and reopen are further limited to the
 * person who raised the query (or a Site Admin) — the server enforces the
 * same rule.
 */
const QUERY_STAFF: readonly UserRole[] = [
  'principal_investigator', 'sub_investigator', 'coordinator', 'regulatory_specialist', 'site_admin', 'site_user',
];
const QUERY_STAFF_AND_REVIEWERS: readonly UserRole[] = [...QUERY_STAFF, 'monitor', 'auditor'];
const CLOSE_OVERRIDE_ROLE = 'site_admin';

export function QueriesTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const perms = useMyPermissions();

  const [statusFilter, setStatusFilter] = useState<'' | ComplianceQueryRow['status']>('');
  const [categoryFilter, setCategoryFilter] = useState<'' | QueryCategory>('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [detailQuery, setDetailQuery] = useState<ComplianceQueryRow | null>(null);
  // Punch-list f3 — "stay linked back to it": jump into the originating
  // binder document from its query row.
  const [viewerDoc, setViewerDoc] = useState<{ binderRef: string; docRef: string; studyId: string } | null>(null);
  // Punch-list f6 — list-convenience filters. "Opened by me" / "Assigned to
  // me" / "Unassigned" are mutually exclusive (each is a distinct WHERE
  // clause server-side); Internal/External is a separate, independent toggle.
  const [quickFilter, setQuickFilter] = useState<'' | 'raised_by_me' | 'assigned_to_me' | 'unassigned'>('');
  const [typeFilter, setTypeFilter] = useState<'' | QueryVisibilityType>('');
  // Punch-list f5 — internal-staff raise flow.
  const [newQueryOpen, setNewQueryOpen] = useState(false);
  const [reopenTarget, setReopenTarget] = useState<ComplianceQueryRow | null>(null);

  const filters = useMemo(
    () => ({
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      assignee_user_id: assigneeFilter.trim() || undefined,
      raised_by_me: quickFilter === 'raised_by_me' || undefined,
      assigned_to_me: quickFilter === 'assigned_to_me' || undefined,
      unassigned: quickFilter === 'unassigned' || undefined,
      type: typeFilter || undefined,
    }),
    [statusFilter, categoryFilter, assigneeFilter, quickFilter, typeFilter],
  );

  const q = useQuery({
    queryKey: ['compliance-queries', filters],
    queryFn: () => compliance.listQueries(filters),
    enabled: !!user,
  });

  const siteUsersQ = useQuery({
    queryKey: ['site-users', 'me'],
    queryFn: siteUsers.listMine,
    staleTime: 60_000,
    enabled: !!user,
  });
  const assigneeOptions = useMemo<SingleSelectOption[]>(
    () =>
      (siteUsersQ.data?.users ?? []).map((u) => ({
        value: u.user_id,
        label: u.full_name,
        sublabel: u.email + (u.status !== 'active' ? ` (${u.status})` : ''),
      })),
    [siteUsersQ.data],
  );

  const respond = useMutation({
    mutationFn: (queryId: string) => compliance.respondQuery(queryId),
    onSuccess: () => {
      toast.success('Query marked as answered.');
      qc.invalidateQueries({ queryKey: ['compliance-queries'] });
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to respond')),
  });

  const closeQuery = useMutation({
    mutationFn: (queryId: string) => compliance.closeQuery(queryId),
    onSuccess: () => {
      toast.success('Query closed.');
      qc.invalidateQueries({ queryKey: ['compliance-queries'] });
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to close')),
  });

  const rows = q.data?.rows ?? [];
  const canRaise = perms.holdsOperation('queries.create', QUERY_STAFF_AND_REVIEWERS);
  const canRespond = perms.holdsOperation('queries.update', QUERY_STAFF);
  const canComment = perms.holdsOperation('queries.comment', QUERY_STAFF_AND_REVIEWERS);
  const canCloseOp = perms.holdsOperation('queries.close', QUERY_STAFF_AND_REVIEWERS);
  const canReopenOp = perms.holdsOperation('queries.reopen', QUERY_STAFF_AND_REVIEWERS);
  /** Raised it, or holds the Site Admin override. */
  const ownsQuery = (row: ComplianceQueryRow) =>
    !!user && (row.raised_by_user_id === user.id || user.role === CLOSE_OVERRIDE_ROLE);
  const canCloseQuery = (row: ComplianceQueryRow) => row.status !== 'closed' && canCloseOp && ownsQuery(row);
  const canReopenQuery = (row: ComplianceQueryRow) => row.status === 'closed' && canReopenOp && ownsQuery(row);

  return (
    <div className="space-y-4">
      {/* Punch-list f5 — internal staff (coordinators, PIs, etc.) can now
          raise queries too, not just monitors/auditors. */}
      {canRaise && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setNewQueryOpen(true)}
            className="inline-flex items-center gap-1.5 rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
            New Query
          </button>
        </div>
      )}

      {/* Phase 4 extended contract (AC-EXT-11) — Query Aging chart. */}
      <QueryAgingChart />

      <div className="card p-4 space-y-3">
        {/* Punch-list f6 — quick filter chips. */}
        <div className="flex flex-wrap items-center gap-2">
          <QuickFilterChip label="Opened by me" active={quickFilter === 'raised_by_me'} onClick={() => setQuickFilter((v) => (v === 'raised_by_me' ? '' : 'raised_by_me'))} />
          <QuickFilterChip label="Assigned to me" active={quickFilter === 'assigned_to_me'} onClick={() => setQuickFilter((v) => (v === 'assigned_to_me' ? '' : 'assigned_to_me'))} />
          <QuickFilterChip label="Unassigned" active={quickFilter === 'unassigned'} onClick={() => setQuickFilter((v) => (v === 'unassigned' ? '' : 'unassigned'))} />
          <span className="mx-1 h-4 w-px bg-surface-border" aria-hidden />
          <QuickFilterChip label="Internal" active={typeFilter === 'internal'} onClick={() => setTypeFilter((v) => (v === 'internal' ? '' : 'internal'))} />
          <QuickFilterChip label="External" active={typeFilter === 'external'} onClick={() => setTypeFilter((v) => (v === 'external' ? '' : 'external'))} />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label text-xs">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="mt-1 rounded border border-surface-border px-3 py-1.5 text-xs"
            >
              <option value="">Any</option>
              <option value="open">Open</option>
              <option value="answered">Answered</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as typeof categoryFilter)}
              className="mt-1 rounded border border-surface-border px-3 py-1.5 text-xs"
            >
              <option value="">Any</option>
              {(Object.keys(CATEGORY_LABELS) as QueryCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="label text-xs">Assignee</label>
            <SingleSelectSearch
              options={assigneeOptions}
              value={assigneeFilter || null}
              onChange={(next) => setAssigneeFilter(next ?? '')}
              placeholder="Any assignee"
              className="mt-1"
            />
          </div>
          <div className="text-xs text-ink-muted">
            {q.isLoading ? (
              <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
            ) : (
              <>{rows.length} query{rows.length === 1 ? '' : 'ies'}</>
            )}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {rows.length === 0 && !q.isLoading ? (
          <div className="p-8 text-center text-sm text-ink-muted">
            No queries match the current filters.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-surface-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Ref</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Type</th>
                <th className="px-3 py-2 text-left font-semibold">Category</th>
                <th className="px-3 py-2 text-left font-semibold">Raised on</th>
                <th className="px-3 py-2 text-left font-semibold">Body</th>
                <th className="px-3 py-2 text-left font-semibold">Assignee</th>
                <th className="px-3 py-2 text-left font-semibold">Raised</th>
                <th className="px-3 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {rows.map((row) => {
                const isClosed = row.status === 'closed';
                const isMyAssignment = !!user && row.assignee_user_id === user.id;
                const canRespondRow =
                  row.status === 'open' &&
                  canRespond &&
                  (!row.assignee_user_id || isMyAssignment);
                const canCloseRow = canCloseQuery(row);
                const canReopenRow = canReopenQuery(row);

                const origin = raisedOn(row);

                return (
                  <tr key={row.query_id} className="hover:bg-surface-muted/30">
                    <td className="px-3 py-2 align-top font-mono text-[11px] text-ink-secondary whitespace-nowrap">
                      {row.reference_number ?? '—'}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[row.status]}`}
                      >
                        {isClosed && <Lock className="h-2.5 w-2.5" />}
                        {STATUS_LABELS[row.status]}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${queryTypeFromOrigin(row.origin) === 'external'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-slate-100 text-slate-700'
                          }`}
                      >
                        {queryTypeFromOrigin(row.origin) === 'external' ? 'External' : 'Internal'}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {row.category ? (
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${CATEGORY_BADGE[row.category]}`}>
                          {CATEGORY_LABELS[row.category]}
                        </span>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-[11px]">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{origin.kind}</div>
                      <div className="text-ink-primary">{origin.where || '—'}</div>
                      {origin.context && <div className="text-ink-muted">{origin.context}</div>}
                      {row.document_id && row.binder_id && (
                        <button
                          type="button"
                          onClick={() =>
                            setViewerDoc({
                              binderRef: row.binder_reference_number || row.binder_id!,
                              docRef: row.document_id!,
                              studyId: row.study_id,
                            })
                          }
                          className="mt-0.5 inline-flex items-center gap-1 text-accent-700 hover:underline"
                          title="Open the document this query was raised on"
                        >
                          Open document
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top max-w-md truncate" title={row.body}>
                      {row.body}
                    </td>
                    <td className="px-3 py-2 align-top text-[11px] text-ink-muted">
                      {row.assignee_user_id
                        ? row.assignee_name ?? row.assignee_user_id.slice(0, 8) + '…'
                        : row.assignee_role
                          ? `role: ${row.assignee_role}`
                          : '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-ink-muted">
                      {fmtDateTime(row.raised_at)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setDetailQuery(row)}
                          className="inline-flex items-center gap-1 rounded border border-surface-border px-2 py-0.5 text-[11px] font-medium hover:bg-surface-muted/50"
                          title="View and add comments"
                        >
                          <MessageSquare className="h-3 w-3" />
                          Comment
                        </button>
                        {canRespondRow && (
                          <button
                            type="button"
                            disabled={respond.isPending}
                            onClick={() => respond.mutate(row.query_id)}
                            className="rounded bg-blue-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            Answer
                          </button>
                        )}
                        {canCloseRow && (
                          <button
                            type="button"
                            disabled={closeQuery.isPending}
                            onClick={() => closeQuery.mutate(row.query_id)}
                            className="rounded bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Close
                          </button>
                        )}
                        {canReopenRow && (
                          <button
                            type="button"
                            onClick={() => setReopenTarget(row)}
                            className="inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-amber-700"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Reopen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detailQuery && (
        <CommentsDrawer
          query={detailQuery}
          canReopen={canReopenQuery(detailQuery)}
          canComment={canComment}
          canClose={canCloseQuery(detailQuery)}
          closing={closeQuery.isPending}
          onCloseQuery={() =>
            closeQuery.mutate(detailQuery.query_id, { onSuccess: () => setDetailQuery(null) })
          }
          onClose={() => setDetailQuery(null)}
        />
      )}

      {reopenTarget && (
        <ReopenQueryDialog
          query={reopenTarget}
          onClose={() => setReopenTarget(null)}
          onReopened={() => {
            setReopenTarget(null);
            qc.invalidateQueries({ queryKey: ['compliance-queries'] });
          }}
        />
      )}

      {viewerDoc && (
        <DocumentViewerModal
          binderRef={viewerDoc.binderRef}
          documentRef={viewerDoc.docRef}
          studyId={viewerDoc.studyId}
          open
          onClose={() => setViewerDoc(null)}
        />
      )}

      {newQueryOpen && (
        <NewInternalQueryDialog
          onClose={() => setNewQueryOpen(false)}
          onRaised={() => {
            setNewQueryOpen(false);
            qc.invalidateQueries({ queryKey: ['compliance-queries'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function QuickFilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${active
        ? 'border-accent-600 bg-accent-600 text-white'
        : 'border-surface-border bg-white text-ink-secondary hover:bg-surface-muted/50'
        }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------

/**
 * Punch-list f5 — internal staff raise flow: study → subject (required,
 * since an internal-only raiser has no folder/document to scope to) →
 * optional assignee (drawn from the study's delegated users) → body.
 */
function NewInternalQueryDialog({ onClose, onRaised }: { onClose: () => void; onRaised: () => void }) {
  const [studyId, setStudyId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [category, setCategory] = useState<QueryCategory>('other');
  const [body, setBody] = useState('');
  // Internal staff raising a query is Internal by default, but the same
  // dialog is reachable by anyone in RESPOND_ROLES — let them flag it
  // External (e.g. relaying a sponsor/CRA concern) instead of guessing
  // from role alone. This dialog is never reachable by monitor/auditor
  // (RESPOND_ROLES excludes them), so the only mismatch to flag here is
  // internal staff picking External.
  const [visibility, setVisibility] = useState<QueryVisibilityType>('internal');
  const visibilityMismatch = visibility === 'external';

  const studiesQ = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: () => studies.list(),
  });
  const subjectsQ = useQuery({
    queryKey: ['subjects', 'list', studyId],
    queryFn: () => subjects.list({ study_id: studyId, pageSize: 200 }),
    enabled: !!studyId,
  });
  const delegatedQ = useQuery({
    queryKey: ['studies', studyId, 'delegated-users'],
    queryFn: () => studies.listDelegatedUsers(studyId),
    enabled: !!studyId,
  });

  const raise = useMutation({
    mutationFn: () =>
      compliance.raiseQuery({
        study_id: studyId,
        subject_id: subjectId || undefined,
        category,
        body: body.trim(),
        assignee_user_id: assigneeUserId || undefined,
        origin: visibility === 'external' ? 'cra_query' : 'internal',
        // Not tied to a folder/document/visit/field — label it plainly so
        // the Section column isn't blank for this raise flow.
        section_ref: 'General',
      }),
    onSuccess: (r) => {
      toast.success(`Query ${r.query_id.slice(0, 8)}… raised.`);
      onRaised();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to raise query')),
  });

  // Only Study, Category, and Description are required — Subject and
  // Assignee are both optional, so a query can be raised at the study
  // level without picking a specific subject.
  const isValid = !!studyId && body.trim().length > 0 && !visibilityMismatch;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-internal-query-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <h3 id="new-internal-query-title" className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <MessageSquarePlus className="h-4 w-4 text-accent-700" />
            New query
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-ink-muted hover:bg-surface-muted/50" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 px-4 py-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Study</span>
            <select
              value={studyId}
              onChange={(e) => { setStudyId(e.target.value); setSubjectId(''); setAssigneeUserId(''); }}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
            >
              <option value="">Select a study…</option>
              {(studiesQ.data?.rows ?? []).map((s: StudyRow) => (
                <option key={s.study_id} value={s.study_id}>{studyLabel(s)}</option>
              ))}
            </select>
          </label>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Subject (optional)</span>
            <select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              disabled={!studyId}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              <option value="">{studyId ? 'Unspecified' : 'Select a study first'}</option>
              {(subjectsQ.data?.rows ?? []).map((s: SubjectRow) => (
                <option key={s.subject_id} value={s.subject_id}>{subjectDisplayId(s)}</option>
              ))}
            </select>
          </label>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Assignee (optional)</span>
            <select
              value={assigneeUserId}
              onChange={(e) => setAssigneeUserId(e.target.value)}
              disabled={!studyId}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              <option value="">Unassigned</option>
              {(delegatedQ.data?.users ?? []).map((u: DelegatedStudyUser) => (
                <option key={u.user_id} value={u.user_id}>{u.full_name} ({u.role})</option>
              ))}
            </select>
          </label>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as QueryCategory)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
            >
              {(Object.keys(CATEGORY_LABELS) as QueryCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </label>

          <fieldset className="text-xs">
            <legend className="font-medium text-ink-secondary">Query type</legend>
            <div className="mt-1 flex gap-4">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="new-query-visibility"
                  checked={visibility === 'internal'}
                  onChange={() => setVisibility('internal')}
                />
                Internal
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="new-query-visibility"
                  checked={visibility === 'external'}
                  onChange={() => setVisibility('external')}
                />
                External
              </label>
            </div>
            {visibilityMismatch && (
              <p className="mt-1.5 rounded border border-danger-200 bg-danger-50 px-2 py-1.5 text-[11px] text-danger-800">
                Site staff can only raise Internal queries.
              </p>
            )}
          </fieldset>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Description</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Describe the query…"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button type="button" onClick={onClose} className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => raise.mutate()}
            disabled={!isValid || raise.isPending}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {raise.isPending ? 'Raising…' : 'Raise query'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReopenQueryDialog({
  query,
  onClose,
  onReopened,
}: {
  query: ComplianceQueryRow;
  onClose: () => void;
  onReopened: () => void;
}) {
  const [reason, setReason] = useState('');

  const reopen = useMutation({
    mutationFn: () => compliance.reopenQuery({ query_id: query.query_id, reason: reason.trim() }),
    onSuccess: () => {
      toast.success('Query reopened.');
      onReopened();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to reopen query')),
  });

  const isValid = reason.trim().length >= 3;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reopen-query-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <h3 id="reopen-query-title" className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <RotateCcw className="h-4 w-4 text-amber-700" />
            Reopen query {query.query_id.slice(0, 8)}…
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-ink-muted hover:bg-surface-muted/50" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 px-4 py-3">
          <p className="truncate text-[11px] text-ink-muted">{query.body}</p>
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Reason for reopening</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Why is this query being reopened?"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button type="button" onClick={onClose} className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => reopen.mutate()}
            disabled={!isValid || reopen.isPending}
            className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {reopen.isPending ? 'Reopening…' : 'Reopen query'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CommentsDrawer({
  query,
  canReopen,
  canComment,
  canClose,
  closing,
  onCloseQuery,
  onClose,
}: {
  query: ComplianceQueryRow;
  canReopen: boolean;
  canComment: boolean;
  /** The caller raised this query (or is a Site Admin) and may close it. */
  canClose: boolean;
  closing: boolean;
  onCloseQuery: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  // Client, 23 Oct 2026: once the query is answered, ask the person who
  // raised it whether they are ready to close it.
  const [closePromptDismissed, setClosePromptDismissed] = useState(false);
  const showClosePrompt = query.status === 'answered' && canClose && !closePromptDismissed;
  const commentsQ = useQuery({
    queryKey: ['compliance-queries', query.query_id, 'comments'],
    queryFn: () => compliance.listComments(query.query_id),
  });

  const add = useMutation({
    mutationFn: () => compliance.addComment({ query_id: query.query_id, body: text.trim() }),
    onSuccess: () => {
      toast.success('Comment added.');
      setText('');
      qc.invalidateQueries({ queryKey: ['compliance-queries', query.query_id, 'comments'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed')),
  });

  const isClosed = query.status === 'closed';

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
              <ClipboardList className="h-4 w-4 text-brand-500" />
              {query.reference_number ?? 'Query'}
              {isClosed && (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                  <Lock className="h-2.5 w-2.5" /> Closed
                </span>
              )}
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-ink-muted">{query.body}</p>
            <p className="mt-0.5 text-[11px] text-ink-secondary">
              Raised on: {(() => {
                const o = raisedOn(query);
                return [o.kind, o.where, o.context].filter(Boolean).join(' · ');
              })()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
          {commentsQ.isLoading && (
            <div className="text-center text-xs text-ink-muted">Loading comments…</div>
          )}
          {commentsQ.data?.rows.length === 0 && !commentsQ.isLoading && (
            <div className="text-center text-xs text-ink-muted">No comments yet.</div>
          )}
          {commentsQ.data?.rows.map((c) => (
            <div
              key={c.comment_id}
              className={`rounded border p-2 ${c.comment_kind === 'reopen'
                ? 'border-amber-200 bg-amber-50'
                : 'border-surface-border bg-surface-muted/30'
                }`}
            >
              <div className="flex items-center justify-between text-[10px] text-ink-muted">
                <span>{c.author_name ?? `${c.author_user_id.slice(0, 8)}…`} · {c.author_role_at_time}</span>
                <span>{fmtDateTime(c.created_at)}</span>
              </div>
              <div className="mt-1 text-xs text-ink-primary">
                {c.comment_kind === 'reopen' ? (
                  <>
                    <span className="inline-flex items-center gap-1 font-semibold text-amber-800">
                      <RotateCcw className="h-3 w-3" /> Reopened
                    </span>
                    {' — '}
                    {c.body}
                  </>
                ) : (
                  c.body
                )}
              </div>
            </div>
          ))}
        </div>

        {showClosePrompt && (
          <div className="border-t border-blue-200 bg-blue-50 px-4 py-3" role="alert">
            <p className="text-xs font-semibold text-blue-900">
              This query has been answered. Are you ready to close it?
            </p>
            <p className="mt-0.5 text-[11px] text-blue-900/80">
              Closing locks the query. Reopening later needs a recorded reason.
            </p>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setClosePromptDismissed(true)}
                className="rounded border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-900 hover:bg-blue-100"
              >
                No, keep open
              </button>
              <button
                type="button"
                disabled={closing}
                onClick={onCloseQuery}
                className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {closing ? 'Closing…' : 'Yes, close query'}
              </button>
            </div>
          </div>
        )}

        <footer className="border-t border-surface-border px-4 py-3">
          {isClosed && (
            <p className="mb-2 text-[11px] text-ink-muted">
              <ShieldCheck className="mr-1 inline h-3 w-3" />
              Closed queries stay open for comments (append-only); commenting does not reopen them.
              {canReopen
                ? ' Use the Reopen action on the query row to reopen it with a recorded reason.'
                : ' Only the person who raised it, or a site admin, can reopen a closed query.'}
            </p>
          )}
          {canComment ? (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                placeholder="Add a comment…"
                className="w-full rounded border border-surface-border px-2 py-1.5 text-xs"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  disabled={!text.trim() || add.isPending}
                  onClick={() => add.mutate()}
                  className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                >
                  {add.isPending ? 'Posting…' : 'Post comment'}
                </button>
              </div>
            </>
          ) : (
            <p className="text-[11px] text-ink-muted">Your role can read these comments but not add to them.</p>
          )}
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 extended contract — Query Aging chart (AC-EXT-11).
// ---------------------------------------------------------------------------

const AGING_BUCKET_COLORS: Record<string, string> = {
  lt_3d: 'bg-emerald-500',
  '3_7d': 'bg-amber-400',
  '7_14d': 'bg-orange-500',
  gt_14d: 'bg-danger-500',
};

function QueryAgingChart() {
  const q = useQuery({
    queryKey: ['compliance-queries', 'aging'],
    queryFn: () => compliance.queryAging(),
    staleTime: 30_000,
  });

  const buckets = q.data?.buckets ?? [];
  const total = q.data?.total ?? 0;
  const maxCount = buckets.reduce((n, b) => Math.max(n, b.count), 0);

  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-brand-500" />
          <h2 className="text-sm font-semibold text-ink-primary">Query aging</h2>
        </div>
        <span className="text-xs text-ink-muted">
          {q.isLoading ? (
            <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
          ) : (
            `${total} open`
          )}
        </span>
      </div>

      {!q.isLoading && total === 0 ? (
        <p className="text-xs text-ink-muted">No open queries.</p>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {buckets.map((b) => {
            const pct = maxCount > 0 ? Math.max(4, Math.round((b.count / maxCount) * 100)) : 4;
            return (
              <div key={b.key} className="flex flex-col items-center">
                <div className="flex h-16 w-full items-end">
                  <div
                    className={`w-full rounded-t ${AGING_BUCKET_COLORS[b.key] ?? 'bg-slate-400'}`}
                    style={{ height: b.count === 0 ? '2px' : `${pct}%` }}
                    aria-hidden
                  />
                </div>
                <div className="mt-1 text-[10px] font-semibold text-ink-primary">{b.count}</div>
                <div className="text-[10px] text-ink-muted">{b.label}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
